import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {debounce} from "lodash";

import CustomDropdown, {DROPDOWN_OPEN_EVENT_NAME, DROPDOWN_POSITION_EVENT_NAME} from "../CustomDropdown";
import LinearLoader from "../LinearLoader";

import './autocomplete.scss';

export const AUTOCOMPLETE_RESET_TRIGGER_NAME = 'mindsets-autocomplete-reset-';

const fetchItemsDebounced = debounce(action => action(), 600);

/**
 *
 * @param id
 * @param fetchItems
 * @param onSelect
 * @param renderItem
 * @param renderAdd
 * @param onReset
 * @param label
 * @param default_value If provided, the input field will be preset with it
 * @param placeholder
 * @param container_class
 * @param input_class
 * @param label_class
 * @param persist_value If TRUE and default_value is provided, the input field will keep its value as well as the search results in dropdown
 * @param show_request_in_dropdown
 * @param with_reset
 * @param disabled
 * @param debounced
 * @param extension
 * @param match_dropdown_width
 * @constructor
 */
const Autocomplete = function (
    {
        id,
        fetchItems,
        onSelect,
        renderItem,
        renderAdd,
        onReset,
        label,
        default_value,
        extension,
        placeholder = '',
        container_class = '',
        input_class = '',
        label_class = '',
        persist_value = false,
        show_request_in_dropdown = false,
        disabled = false,
        debounced = true,
        match_dropdown_width = false
    }
) {
    const [input, setInput] = useState(default_value ? default_value : '');
    const [items, setItems] = useState([]);
    const [isRequested, setIsRequested] = useState(false);
    const [requesting, setRequesting] = useState(false);
    const [mode, setMode] = useState('collapsed');

    function reset() {
        setInput('');
        setItems([]);
        setIsRequested(false);
        setRequesting(false);
        setMode('collapsed')
        if (typeof onReset === 'function') {
            onReset();
        }
    }

    function renderTrigger() {
        return (
            <div className="autocomplete-input">
                {renderInputLabel()}
                <div className='autocomplete-input-wrapper'>
                    <input
                        type="text"
                        autoComplete="off"
                        className={"autocomplete-input-field " + input_class}
                        id="autocomplete-input-field"
                        value={input}
                        onChange={event => {
                            event.preventDefault();
                            onInputChange(event.target.value);
                        }}
                        placeholder={placeholder}
                        disabled={disabled}
                    />
                    {renderExtendButton()}
                    {renderResetButton()}
                </div>
            </div>
        );
    }

    function action(value) {
        if (value.length >= 3) {
            setIsRequested(false);
            setRequesting(true);
            fetchItems(value)
                .then(items => {
                    setIsRequested(true);
                    setRequesting(false);
                    setItems(items);
                    if (id) {
                        window.dispatchEvent(new CustomEvent(DROPDOWN_POSITION_EVENT_NAME + id));
                    }
                })
                .catch(error => {
                    console.error(error);
                    setIsRequested(false);
                    setRequesting(false);
                });
        }
    }

    function onInputChange(value) {
        setInput(value);
        setItems([]);
        setIsRequested(false);
        if (id) {
            window.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT_NAME + id));
        }
        if (value.length >= 3) {
            if (debounced) {
                fetchItemsDebounced(() => action(value))
            } else {
                action(value);
            }
        }
    }

    function onChoose(item) {
        setInput(onSelect(item));
        setIsRequested(persist_value);
        if (!persist_value) {
            setItems([]);
        }
    }

    function renderInputLabel() {
        if (label) {
            return (
                <label className={label_class}>{label}</label>
            );
        }

        return null;
    }

    function renderContent() {
        let objects = {};
        if (input.length < 3) {
            objects['more-letters'] = (
                <div className="autocomplete-dropdown-menu-head">Please enter at least 3 letters</div>);
        } else if (isRequested && !requesting && items.length <= 0) {
            if (show_request_in_dropdown) {
                const item = {
                    email: input
                };
                objects['request-item'] = (
                    <div
                        onClick={event => {
                            event.preventDefault();
                            onChoose(item);
                        }}>
                        {renderItem(item)}
                    </div>
                );
            } else {
                objects['no-items'] = (<div className="autocomplete-dropdown-menu-head">Nothing found</div>);
            }
        } else if (isRequested && !requesting && items.length > 0) {
        } else if (requesting) {
            objects['not-found'] = (
                <div className="autocomplete-dropdown-menu-head">
                    <div>Searching ...</div>
                    <LinearLoader/>
                </div>
            );
        } else {
            objects['continue'] = (<div className="autocomplete-dropdown-menu-head">Finish typing</div>);
        }
        items.map((item, index) => {
            objects['z' + index] = (
                <div onClick={event => {
                    event.preventDefault();
                    onChoose(item);
                }}>
                    {renderItem(item)}
                </div>
            );
        });

        if (typeof renderAdd === 'function' && isRequested && !requesting && (items.length <= 0)) {
            objects['add'] = renderAdd(input => {
                setInput(input);
                setIsRequested(persist_value);
                if (!persist_value) {
                    setItems([]);
                }
            });
        }

        return objects;
    }

    function renderResetButton() {
        if (typeof onReset === 'function') {
            return (
                <i
                    className='clickable autocomplete-reset-icon fas fa-times-circle'
                    onClick={reset}
                />
            );
        }

        return null;
    }

    function renderExtendButton() {
        if (!!extension) {
            return (
                <i
                    className='clickable fas fa-sliders-h autocomplete-extend-icon'
                    onClick={event => {
                        if (mode === 'collapsed') {
                            setMode('extended');
                            window.dispatchEvent(new CustomEvent(DROPDOWN_OPEN_EVENT_NAME + id));
                        } else {
                            setMode('collapsed');
                        }
                        event.stopPropagation();
                    }}/>
            );
        }

        return null;
    }

    useEffect(() => {
        if (id && typeof onReset === 'function') {
            window.addEventListener(AUTOCOMPLETE_RESET_TRIGGER_NAME + id, reset);
            return () => window.removeEventListener(AUTOCOMPLETE_RESET_TRIGGER_NAME + id, reset)
        }
    }, []);

    return (
        <div className={"autocomplete " + container_class}>
            <CustomDropdown
                id={id}
                trigger={renderTrigger()}
                no_wrap={true}
                content_class='autocomplete-dropdown-menu'
                item_class='autocomplete-dropdown-menu-item'
                items={renderContent()}
                disabled={disabled}
                before_trigger={mode === 'extended' && extension ? extension : null}
                match_dropdown_width={match_dropdown_width}
                use_arrow={false}
            />
        </div>
    );
}

Autocomplete.propTypes = {
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    placeholder: PropTypes.string,
    container_class: PropTypes.string,
    input_class: PropTypes.string,
    label_class: PropTypes.string,
    fetchItems: PropTypes.func.isRequired,
    onSelect: PropTypes.func.isRequired,
    renderItem: PropTypes.func.isRequired,
    renderAdd: PropTypes.func,
    onReset: PropTypes.func,
    label: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.number,
        PropTypes.object,
        PropTypes.element,
    ]),
    default_value: PropTypes.string,//If provided, the input field will be preset with it
    persist_value: PropTypes.bool,//If TRUE and default_value is provided, the input field will keep its value as well as the search results in dropdown
    show_request_in_dropdown: PropTypes.bool,
    always_show_add: PropTypes.bool,
    disabled: PropTypes.bool,
    debounced: PropTypes.bool,
    extension: PropTypes.any,
    match_dropdown_width: PropTypes.bool,
}

export default Autocomplete;
